import React, { useState } from 'react';
import {
  LineChart,
  Line,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const FavoritesTrendGraph = ({ data, timeRange = 30 }) => {
  const [chartType, setChartType] = useState('area');

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No trend data available yet</p>
        <p className="text-sm">Start getting favorites to see trends here</p>
      </div>
    );
  }

  const formattedData = data.map(item => ({
    ...item,
    date: new Date(item.date).toLocaleDateString('en-AU', { 
      month: 'short', 
      day: 'numeric' 
    })
  }));

  const totalNewFavorites = formattedData.reduce((sum, d) => sum + d.newFavorites, 0);
  const averageNewPerDay = (totalNewFavorites / formattedData.length).toFixed(1);
  const lastDayNew = formattedData[formattedData.length - 1]?.newFavorites || 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-gray-700">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="favorites-trend-graph">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="border rounded-lg p-3 bg-gray-50">
          <p className="text-xs text-gray-500">New Favorites (Last {timeRange} Days)</p>
          <p className="text-lg font-semibold">{totalNewFavorites}</p>
        </div>
        <div className="border rounded-lg p-3 bg-gray-50">
          <p className="text-xs text-gray-500">Average Per Day</p>
          <p className="text-lg font-semibold">{averageNewPerDay}</p>
        </div>
        <div className="border rounded-lg p-3 bg-gray-50">
          <p className="text-xs text-gray-500">Most Recent Day</p>
          <p className="text-lg font-semibold">{lastDayNew}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-gray-700">📈 Favorites Trend</h4>
        <div className="flex gap-2">
          <button
            onClick={() => setChartType('area')}
            className={`px-3 py-1 text-xs rounded-md transition ${
              chartType === 'area' 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Area
          </button>
          <button
            onClick={() => setChartType('line')}
            className={`px-3 py-1 text-xs rounded-md transition ${
              chartType === 'line' 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Line
          </button>
        </div>
      </div>

      {/* Fix: Added min-height and proper container styling */}
      <div className="h-64 w-full min-h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'area' ? (
            <AreaChart data={formattedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area
                type="monotone"
                dataKey="newFavorites"
                name="New Favorites"
                stroke="#22c55e"
                fill="#22c55e"
                fillOpacity={0.2}
              />
              <Area
                type="monotone"
                dataKey="totalActive"
                name="Total Active"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.1}
              />
            </AreaChart>
          ) : (
            <LineChart data={formattedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="newFavorites"
                name="New Favorites"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="totalActive"
                name="Total Active"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="flex justify-center gap-6 mt-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-green-500" />
          New Favorites
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-blue-500" />
          Total Active
        </span>
      </div>
    </div>
  );
};

export default FavoritesTrendGraph;
